<script setup lang="ts">
import Icon from './Icon.vue';

/**
 * Recoverable-error placeholder: shows the correlation id (so the user can quote it to support —
 * RFC 9457 problem responses always carry one, ADR-021) and a retry action.
 */
defineProps<{
  title: string;
  description?: string;
  correlationId?: string | null;
  retryLabel?: string;
  copyLabel?: string;
}>();

defineEmits<{ retry: [] }>();
async function copyCorrelationId(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    /* The visible ID remains selectable when clipboard permission is unavailable. */
  }
}
</script>

<template>
  <div
    class="jv-state flex flex-col items-center gap-4 rounded-jv-lg bg-jv-surface-lowest px-6 py-10 text-center"
    role="alert"
    data-test="error-state"
  >
    <span class="grid size-24 place-items-center rounded-full bg-jv-danger-container/30 text-jv-danger">
      <Icon name="warning" :size="44" />
    </span>
    <div>
      <p class="m-0 text-lg leading-[26px] font-semibold text-jv-on-surface">{{ title }}</p>
      <p v-if="description" class="m-0 mt-1 max-w-sm text-sm text-jv-on-surface-variant">{{ description }}</p>
      <div v-if="correlationId" class="mt-4 flex items-center justify-center gap-3 rounded-jv-lg bg-jv-surface-low p-3">
        <code class="select-all break-all text-xs text-jv-on-surface-variant" dir="ltr">{{ correlationId }}</code
        ><button
          v-if="copyLabel"
          type="button"
          :aria-label="copyLabel"
          class="shrink-0 text-jv-primary"
          @click="copyCorrelationId(correlationId)"
        >
          <Icon name="content_copy" :size="18" />
        </button>
      </div>
    </div>
    <button
      v-if="retryLabel"
      type="button"
      class="rounded-jv-md bg-jv-primary px-4 py-2 text-sm font-medium text-jv-on-primary"
      @click="$emit('retry')"
    >
      {{ retryLabel }}
    </button>
  </div>
</template>
